/**
 * ParagraphFollowCursor — thin gold vertical line that slides down the
 * left margin alongside the currently-narrated paragraph on /demo.
 *
 * Apr 30 2026 iter_61 — Lead's ask: "Yes you can do the follow mode.
 * With the thin gold cursor." A cinematic tell of where Midnite is in
 * the episode without forcing the page to scroll (auto-scroll was
 * removed in iter_59).
 *
 * Behaviour:
 *   • Every requestAnimationFrame tick, finds the single
 *     `[data-para-state=active]` paragraph in the DOM.
 *   • Positions itself as a fixed-viewport 2px gold line at that
 *     paragraph's vertical span, with a soft glow + pulse.
 *   • Hides gracefully when no paragraph is active (idle audio, end
 *     of episode, or user navigated away from the demo page).
 *
 * Mounts once at the page level. Zero impact on layout — uses
 * `position: fixed` + `pointer-events: none`.
 */
import { useEffect, useState } from 'react';

export default function ParagraphFollowCursor({ testId = 'paragraph-follow-cursor' }) {
  const [rect, setRect] = useState(null);

  useEffect(() => {
    let raf;
    const tick = () => {
      const el = document.querySelector('[data-para-state=active]');
      if (el) {
        const r = el.getBoundingClientRect();
        // Clamp to viewport so the cursor never peeks above/below fold.
        const top = Math.max(0, r.top);
        const bottom = Math.min(window.innerHeight, r.bottom);
        setRect({ top, height: Math.max(24, bottom - top), visible: bottom > 0 && top < window.innerHeight });
      } else {
        setRect(null);
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  if (!rect || !rect.visible) return null;
  return (
    <div
      data-testid={testId}
      aria-hidden="true"
      style={{
        position: 'fixed',
        left: 'max(18px, calc((100vw - 900px) / 2 - 28px))',
        top: rect.top,
        height: rect.height,
        width: 2,
        background: 'linear-gradient(to bottom, rgba(212,168,74,0) 0%, #D4A84A 18%, #FFE08A 50%, #D4A84A 82%, rgba(212,168,74,0) 100%)',
        boxShadow: '0 0 14px rgba(212,168,74,0.65), 0 0 32px rgba(212,168,74,0.25)',
        borderRadius: 1,
        pointerEvents: 'none',
        zIndex: 40,
        transition: 'top 520ms cubic-bezier(0.65,0,0.35,1), height 520ms cubic-bezier(0.65,0,0.35,1)',
        willChange: 'top, height',
      }}
    />
  );
}
